/**
 * Product Gallery Component
 *
 * Implements a two-column product gallery layout with lightbox functionality.
 * Left column: Main image at top, 2 thumbnails per row below
 * Right column: Product details
 */

(() => {
    function registerProductGallery() {
        Alpine.data('productGallery', () => ({
            // Gallery state
            images: [],
            currentImageIndex: 0,
            pswp: null,

            // Initialize the component
            init() {
                this.loadGalleryImages();
                this.setupEventListeners();
                this.initializeLightbox();
                this.ensureImagesLoaded();
            },

            // Ensure all images are properly loaded
            ensureImagesLoaded() {
                // Wait a bit for images to load, then check dimensions
                setTimeout(() => {
                    this.validateImageDimensions();
                }, 500);
            },

            // Validate and fix image dimensions
            validateImageDimensions() {
                this.images.forEach((img, index) => {
                    if (img && (img.w <= 1 || img.h <= 1)) {
                        // Try to get dimensions from the actual DOM element
                        const domImg =
                            this.$el.querySelector(`img[data-index="${index}"]`) ||
                            this.$el.querySelector(`img[data-index="${index - 1}"]`);

                        if (domImg && domImg.naturalWidth > 1 && domImg.naturalHeight > 1) {
                            img.w = domImg.naturalWidth;
                            img.h = domImg.naturalHeight;
                        } else {
                            // Use fallback dimensions
                            img.w = 800;
                            img.h = 600;
                        }
                    }
                });

                // Debug: Log image information
                if (this.images.length > 0) {
                    console.log(
                        'Gallery images loaded:',
                        this.images.map((img) => ({
                            src: img.src,
                            dimensions: `${img.w}x${img.h}`,
                            title: img.title,
                        }))
                    );
                }
            },

            // Load gallery images from WooCommerce
            loadGalleryImages() {
                const productId = this.$el.dataset.productId;
                if (!productId) return;

                // Get main product image
                const mainImage = this.$el.querySelector('.main-image-container img');
                if (mainImage) {
                    this.addImageToGallery(mainImage, 0);
                }

                // Get gallery thumbnails
                const thumbnails = this.$el.querySelectorAll('.thumbnail-item img');
                thumbnails.forEach((img, index) => {
                    if (img) {
                        this.addImageToGallery(img, index + 1);
                    }
                });
            },

            // Add image to gallery with proper dimension handling
            addImageToGallery(img, index) {
                const imageData = {
                    alt: img.alt || '',
                    src: img.src,
                    full: img.dataset.full || img.src,
                    title: img.dataset.title || '',
                    index: index,
                };

                // Get dimensions from data attributes first (more reliable)
                const dataWidth = parseInt(img.dataset.width);
                const dataHeight = parseInt(img.dataset.height);

                if (dataWidth && dataHeight && dataWidth > 1 && dataHeight > 1) {
                    imageData.w = dataWidth;
                    imageData.h = dataHeight;
                    this.images[index] = imageData;
                    return;
                }

                // Handle image dimensions
                if (img.complete && img.naturalWidth > 0) {
                    // Image already loaded
                    imageData.w = img.naturalWidth;
                    imageData.h = img.naturalHeight;
                    this.images[index] = imageData;
                } else {
                    // Image still loading, wait for it
                    const handleImageLoad = () => {
                        imageData.w = img.naturalWidth || 800; // Fallback width
                        imageData.h = img.naturalHeight || 600; // Fallback height
                        this.images[index] = imageData;

                        // Remove event listener
                        img.removeEventListener('load', handleImageLoad);
                        img.removeEventListener('error', handleImageError);
                    };

                    const handleImageError = () => {
                        // Fallback dimensions if image fails to load
                        imageData.w = 800;
                        imageData.h = 600;
                        this.images[index] = imageData;

                        // Remove event listener
                        img.removeEventListener('load', handleImageLoad);
                        img.removeEventListener('error', handleImageError);
                    };

                    // Add event listeners
                    img.addEventListener('load', handleImageLoad);
                    img.addEventListener('error', handleImageError);

                    // If image is already loaded but naturalWidth is 0, use fallback
                    if (img.complete && img.naturalWidth === 0) {
                        imageData.w = 800;
                        imageData.h = 600;
                        this.images[index] = imageData;
                    }
                }
            },

            // Setup event listeners
            setupEventListeners() {
                // Handle thumbnail clicks for image switching
                this.$el.addEventListener('click', (e) => {
                    const thumbnail = e.target.closest('.thumbnail-item');
                    if (thumbnail) {
                        e.preventDefault();
                        const index = parseInt(thumbnail.querySelector('img').dataset.index) || 0;
                        this.setCurrentImage(index);
                    }
                });
            },

            // Initialize WooCommerce lightbox
            initializeLightbox() {
                // Wait for WooCommerce scripts to load
                if (typeof wc_single_product_params !== 'undefined') {
                    this.setupWooCommerceLightbox();
                } else {
                    // Retry after a short delay
                    setTimeout(() => {
                        this.initializeLightbox();
                    }, 100);
                }
            },

            // Setup WooCommerce lightbox
            setupWooCommerceLightbox() {
                // Initialize PhotoSwipe if available
                if (typeof PhotoSwipe !== 'undefined') {
                    this.initPhotoSwipe();
                } else {
                    // Retry after a short delay
                    setTimeout(() => {
                        this.setupWooCommerceLightbox();
                    }, 200);
                }
            },

            // Initialize PhotoSwipe lightbox
            initPhotoSwipe() {
                const gallery = this.$el;
                if (!gallery) return;

                // Get all lightbox triggers
                const triggers = gallery.querySelectorAll('.gallery__trigger');

                triggers.forEach((trigger, index) => {
                    trigger.addEventListener('click', (e) => {
                        e.preventDefault();
                        this.openLightbox(index);
                    });
                });
            },

            // Open lightbox with specific image
            openLightbox(index) {
                if (this.images.length === 0) return;

                // Filter out images that don't have proper dimensions
                const validImages = this.images.filter((img) => img && img.w > 0 && img.h > 0);

                if (validImages.length === 0) {
                    console.warn('No valid images found for lightbox');
                    return;
                }

                // Find the correct index in valid images
                const validIndex = Math.min(index, validImages.length - 1);

                // Create PhotoSwipe items array
                const items = validImages.map((img) => ({
                    src: img.full,
                    w: img.w,
                    h: img.h,
                    title: img.title || img.alt,
                }));

                // PhotoSwipe options
                const options = {
                    index: validIndex,
                    bgOpacity: 0.85,
                    showHideOpacity: true,
                    history: false,
                    shareEl: false,
                    fullscreenEl: true,
                    zoomEl: true,
                    counterEl: true,
                    arrowEl: true,
                    preloaderEl: true,
                    closeOnScroll: false,
                    closeOnVerticalDrag: true,
                    pinchToClose: true,
                    allowPanToNext: true,
                    maxSpreadZoom: 3,
                    getThumbBoundsFn: (index) => {
                        const thumbnail = this.$el.querySelectorAll('.gallery__trigger')[index];
                        if (!thumbnail) return null;

                        const rect = thumbnail.getBoundingClientRect();
                        return {
                            x: rect.left,
                            y: rect.top,
                            w: rect.width,
                        };
                    },
                };

                // Initialize PhotoSwipe
                this.pswp = new PhotoSwipe(
                    document.querySelector('.pswp'),
                    PhotoSwipeUI_Default,
                    items,
                    options
                );

                // Open PhotoSwipe
                this.pswp.init();
            },

            // Set current image (for thumbnails)
            setCurrentImage(index) {
                if (index >= 0 && index < this.images.length) {
                    this.currentImageIndex = index;

                    // Update main image
                    const mainImage = this.$el.querySelector('.main-image-container img');
                    if (mainImage) {
                        mainImage.src = this.images[index].src;
                        mainImage.alt = this.images[index].alt;
                    }

                    // Update active thumbnail
                    this.updateActiveThumbnail(index);
                }
            },

            // Update active thumbnail styling
            updateActiveThumbnail(index) {
                const thumbnails = this.$el.querySelectorAll('.thumbnail-item');
                thumbnails.forEach((thumb, i) => {
                    if (i === index) {
                        thumb.classList.add('active');
                    } else {
                        thumb.classList.remove('active');
                    }
                });
            },
        }));
    }

    if (typeof Alpine !== 'undefined' && Alpine.version) {
        registerProductGallery();
    } else {
        document.addEventListener('alpine:init', () => {
            registerProductGallery();
        });
    }
})();
